import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import type { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";
import { getDocsUrl, getSupportEmail } from "../utils/domainConfig";

// ============================================================
// Constants
// ============================================================

/** Header name expected by the backend TurnstileGuard (legacy per-request flow) */
export const TURNSTILE_HEADER = "cf-turnstile-response";

/**
 * Regex matching /grants/calls paths that require Turnstile token.
 * Matches: /grants/calls, /grants/calls/open, /grants/calls/upcoming,
 * /grants/calls/archive, /grants/calls/:id, /grants/calls?query=...
 * Does NOT match: /admin/grants/calls, /grants/programs, etc.
 */
export const GRANTS_CALLS_PATH_PATTERN = /^\/grants\/calls(\/[^?]*)?(\?.*)?$/;

export const REGISTRATION_PATH_PATTERN = /^\/register\/(create|create-direct|check-eligibility)$/;

/**
 * Token refresh interval in milliseconds.
 * Turnstile tokens expire after 5 minutes;
 * we refresh at 4 minutes to stay safely within the window.
 */
export const TOKEN_REFRESH_INTERVAL_MS = 4 * 60 * 1000;

/** Max time (ms) to wait for Turnstile token before sending request without it */
const TOKEN_WAIT_TIMEOUT_MS = 5000;

/** Polling interval (ms) when waiting for Turnstile token */
const TOKEN_POLL_INTERVAL_MS = 200;

// ============================================================
// Module-level token store
// Shared between React provider (writes) and axios interceptor (reads)
// ============================================================

let currentToken: string | null = null;
let turnstileReady = false;

/**
 * Module-level mutable references for the cookie manager bridge.
 * These are set by TurnstileProvider and used by the cookie manager
 * (via apiClient.ts) to call widget.reset() and get new tokens.
 */
const bridge = {
  resetWidget: (): void => {
    // No-op until TurnstileProvider mounts
  },
  forceRemount: (): void => {
    // No-op until TurnstileProvider mounts
  },
  handleNewToken: null as ((token: string) => Promise<void>) | null,
  onCookieReady: null as ((ready: boolean) => void) | null,
  onError: null as (() => void) | null,
  getLastVerifyTimestamp: (): number => 0,
  /**
   * Clear the cached Turnstile token. Used after a protected endpoint
   * consumes the token via Cloudflare siteverify (single-use semantics)
   * to prevent re-use on subsequent requests within the same widget lifetime.
   *
   * Operates on the module-level `currentToken` directly — no Provider
   * dependency, so it is safe to call from response interceptors.
   */
  invalidateToken: (): void => {
    currentToken = null;
  },
};

/** Get the bridge - used by apiClient.ts to create the cookie manager */
export function getTurnstileBridge() {
  return bridge;
}

/** Get the current Turnstile token (used by axios interceptor) */
export function getTurnstileToken(): string | null {
  if (currentToken) {
    return currentToken;
  }
  // Fallback: read directly from Turnstile API when React state is out of sync
  if (typeof window !== "undefined" && window.turnstile) {
    try {
      const apiToken = window.turnstile.getResponse();
      if (apiToken) {
        return apiToken;
      }
    } catch {
      // api.js throws TurnstileError "Could not find widget" while no widget is
      // rendered (before first render, or between unmount and remount). Treat it
      // as "no token yet" - a throw inside the polling interval would skip
      // clearInterval and leave the request waiting forever (Sentry KN-FRONTEND-8).
    }
  }
  return null;
}

/** Cancel callbacks of in-flight waitForTurnstileToken() polls */
const pendingTokenWaits = new Set<() => void>();

/**
 * Stop all in-flight token polls; their promises resolve with null so queued
 * requests continue without a token. Called when TurnstileProvider unmounts.
 */
export function cancelPendingTokenWaits(): void {
  Array.from(pendingTokenWaits).forEach((cancel) => cancel());
}

/**
 * Wait for Turnstile token to become available (up to timeout).
 * Polls both React state and Turnstile API.
 */
export function waitForTurnstileToken(): Promise<string | null> {
  // Token already available
  const immediate = getTurnstileToken();
  if (immediate) return Promise.resolve(immediate);

  return new Promise((resolve) => {
    let elapsed = 0;
    const finish = (token: string | null) => {
      clearInterval(interval);
      pendingTokenWaits.delete(cancel);
      resolve(token);
    };
    const cancel = () => finish(null);
    const interval = setInterval(() => {
      elapsed += TOKEN_POLL_INTERVAL_MS;
      const token = getTurnstileToken();
      if (token) {
        finish(token);
      } else if (elapsed >= TOKEN_WAIT_TIMEOUT_MS) {
        finish(null); // Timeout - send without token
      }
    }, TOKEN_POLL_INTERVAL_MS);
    pendingTokenWaits.add(cancel);
  });
}

/** Check if the Turnstile widget has completed initial verification */
export function isTurnstileReady(): boolean {
  return turnstileReady;
}

// ============================================================
// Context
// ============================================================

interface TurnstileContextValue {
  /** Current Turnstile token, or null if not yet resolved */
  token: string | null;
  /** Whether the Turnstile widget failed to load */
  error: boolean;
  /** Whether the Turnstile widget has received its first token (or siteKey is empty) */
  isReady: boolean;
  /** Whether the browser verification cookie is set (cookie flow) */
  isCookieReady: boolean;
}

const TurnstileContext = createContext<TurnstileContextValue | undefined>(
  undefined
);

// ============================================================
// Interceptor factory (pure function, testable without React)
// Legacy: kept for backward compatibility during migration.
// ============================================================

/**
 * Creates an axios request interceptor that attaches the Turnstile token.
 *
 * @deprecated This interceptor is for the legacy per-request flow.
 * The cookie-based flow does not need per-request tokens on grants/calls.
 * Kept for backward compatibility during migration period.
 */
export function createTurnstileRequestInterceptor(
  getToken: () => string | null,
  waitForToken: () => Promise<string | null> = waitForTurnstileToken
) {
  return async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    const url = config.url ?? "";
    let token: string | null;

    if ((GRANTS_CALLS_PATH_PATTERN.test(url) || REGISTRATION_PATH_PATTERN.test(url)) && isTurnstileEnabled()) {
      // Protected endpoint: wait for token (only when Turnstile is enabled)
      token = await waitForToken();
    } else {
      // Other endpoints: attach if available, don't wait
      token = getToken();
    }

    if (token) {
      config.headers[TURNSTILE_HEADER] = token;
    }

    return config;
  };
}

// ============================================================
// Response interceptor factory (token invalidation)
// ============================================================

/**
 * Creates an axios response interceptor (success path) that invalidates the
 * Turnstile token after a 2xx response from a protected endpoint.
 *
 * Why: Cloudflare Turnstile per-request tokens are SINGLE-USE. Once the
 * backend's TurnstileCookieGuard sends a token to `siteverify`, that token
 * is permanently consumed. Without explicit invalidation, the frontend's
 * module-level `currentToken` continues to hold the consumed value and
 * sends it on the next protected request (e.g., when a user navigates back
 * in a multi-step wizard and re-submits step 1) → 403
 * `TURNSTILE_COOKIE_MISSING` (misleading: token was sent but rejected as
 * already-redeemed).
 *
 * The interceptor:
 *  1. Calls `invalidateToken()` to clear `currentToken`
 *  2. Calls `resetWidget()` to ask Cloudflare for a fresh token in the
 *     background (invisible widget reset, sub-second)
 *
 * Errors are NOT handled here — they fall through to the next response
 * interceptor in the chain (`turnstileCookieManager.createResponseInterceptor`),
 * which already triggers re-challenge on 403 with retryable error codes.
 *
 * Regression: Bug reproduced 2026-05-08 (Agnieszka Lebryk) and 2026-05-09
 *   (internal QA) — Safari user clicks "Dalej" on registration step 1,
 *   GUS lookup returns empty data (separate bug), user navigates back,
 *   clicks "Dalej" again → "Turnstile verification failed".
 */
export function createTurnstileTokenInvalidationInterceptor(
  invalidateToken: () => void,
  resetWidget: () => void,
  isProtectedPath: (url: string) => boolean = (url) =>
    REGISTRATION_PATH_PATTERN.test(url) ||
    GRANTS_CALLS_PATH_PATTERN.test(url),
) {
  return (response: AxiosResponse): AxiosResponse => {
    const url = response.config?.url ?? "";
    if (isProtectedPath(url)) {
      invalidateToken();
      resetWidget();
    }
    return response;
  };
}

// ============================================================
// Provider
// ============================================================

interface TurnstileProviderProps {
  /** Cloudflare Turnstile site key (public) */
  siteKey: string;
  children: React.ReactNode;
}

/**
 * Provides Turnstile token management to the component tree.
 * Renders an invisible Turnstile widget, manages cookie-based verification
 * via the bridge pattern, and displays error banners.
 *
 * The cookie flow:
 * 1. Widget resolves -> onSuccess -> handleSuccess
 * 2. handleSuccess sets token + calls bridge.handleNewToken (if connected)
 * 3. Cookie manager (in apiClient.ts) receives token -> POST /verify-browser
 * 4. On success -> setCookieReady(true) via bridge.onCookieReady
 * 5. Proactive refresh: cookie manager calls bridge.resetWidget after 4 min
 */
export const TurnstileProvider: React.FC<TurnstileProviderProps> = ({
  siteKey,
  children,
}) => {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [isReady, setIsReady] = useState(!siteKey);
  const [cookieReady, setCookieReady] = useState(!siteKey);
  const [remountKey, setRemountKey] = useState(0);
  const widgetRef = useRef<TurnstileInstance | null>(null);

  const forceRemount = useCallback(() => {
    Sentry.addBreadcrumb({ category: "turnstile", message: "force_remount" });
    setRemountKey((k) => k + 1);
  }, []);

  // Connect bridge to widget ref and React state
  useEffect(() => {
    if (!siteKey) return;

    bridge.resetWidget = () => {
      if (widgetRef.current) {
        widgetRef.current.reset();
      }
    };

    bridge.forceRemount = forceRemount;

    bridge.onCookieReady = (ready: boolean) => {
      setCookieReady(ready);
    };

    bridge.onError = () => {
      setError(true);
    };

    return () => {
      bridge.resetWidget = () => {};
      bridge.forceRemount = () => {};
      bridge.onCookieReady = null;
      bridge.onError = null;
    };
  }, [siteKey, forceRemount]);

  // Stop pending token polls when the provider unmounts
  useEffect(() => cancelPendingTokenWaits, []);

  // Force remount widget when returning from background tab with expired cookie
  useEffect(() => {
    if (!siteKey) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      const lastVerify = bridge.getLastVerifyTimestamp();
      if (lastVerify === 0) return; // Never verified — widget may still be initializing

      const elapsed = Date.now() - lastVerify;
      if (elapsed > TOKEN_REFRESH_INTERVAL_MS) {
        Sentry.addBreadcrumb({
          category: "turnstile",
          message: "visibility_change_remount",
          data: { elapsedMs: elapsed },
        });
        forceRemount();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [siteKey, forceRemount]);

  // Sync React state to module-level store for axios interceptor
  useEffect(() => {
    currentToken = token;
  }, [token]);

  // Sync isReady to module-level store for axios interceptor
  useEffect(() => {
    turnstileReady = isReady;
  }, [isReady]);

  const handleSuccess = useCallback((newToken: string) => {
    Sentry.addBreadcrumb({ category: "turnstile", message: "widget_success" });
    setToken(newToken);
    setError(false);
    setIsReady(true);

    // Cookie flow: notify cookie manager about new token
    if (bridge.handleNewToken) {
      bridge.handleNewToken(newToken);
    }
  }, []);

  const handleError = useCallback(() => {
    Sentry.captureMessage("Turnstile widget error", {
      level: "warning",
      tags: { component: "turnstile" },
    });
    setError(true);
  }, []);

  const handleExpire = useCallback(() => {
    Sentry.addBreadcrumb({ category: "turnstile", message: "widget_expire" });
    setToken(null);
  }, []);

  const contextValue: TurnstileContextValue = {
    token,
    error,
    isReady,
    isCookieReady: cookieReady,
  };

  return (
    <TurnstileContext.Provider value={contextValue}>
      {error && (
        <div
          role="alert"
          aria-live="assertive"
          data-testid="turnstile-error-banner"
          className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-center text-sm"
        >
          Nie można zweryfikować przeglądarki. Wyłącz rozszerzenia blokujące
          (np. AdBlock, Brave Shields) i odśwież stronę.{" "}
          {getDocsUrl("/troubleshooting/weryfikacja-przegladarki") && (
            <>
              <a
                href={getDocsUrl("/troubleshooting/weryfikacja-przegladarki")}
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                Dowiedz się więcej
              </a>
              {" | "}
            </>
          )}
          <a
            href={`mailto:${getSupportEmail()}`}
            className="underline font-medium"
          >
            {getSupportEmail()}
          </a>
        </div>
      )}
      {siteKey && (
        <Turnstile
          key={remountKey}
          ref={widgetRef}
          siteKey={siteKey}
          onSuccess={handleSuccess}
          onError={handleError}
          onExpire={handleExpire}
          options={{
            size: "invisible",
          }}
        />
      )}
      {children}
    </TurnstileContext.Provider>
  );
};

// ============================================================
// Hook
// ============================================================

/**
 * Access the Turnstile context values.
 * Must be used within a TurnstileProvider.
 */
export function useTurnstileContext(): TurnstileContextValue {
  const context = useContext(TurnstileContext);
  if (context === undefined) {
    throw new Error(
      "useTurnstileContext must be used within a TurnstileProvider"
    );
  }
  return context;
}

// ============================================================
// Helpers to get config from env
// ============================================================

/**
 * Check if Turnstile is explicitly enabled via environment variable.
 * Requires REACT_APP_GRANTS_TURNSTILE_ENABLED=true to activate.
 * Defaults to false (disabled) when not set.
 */
export function isTurnstileEnabled(): boolean {
  const value =
    process.env.REACT_APP_GRANTS_TURNSTILE_ENABLED ??
    (typeof window !== "undefined"
      ? window._env_?.REACT_APP_GRANTS_TURNSTILE_ENABLED
      : undefined);

  return value === "true";
}

/**
 * Get the Turnstile site key from environment variables.
 * Supports both CRA build-time (process.env) and runtime (window._env_) patterns.
 *
 * Returns empty string when Turnstile is disabled (isTurnstileEnabled() === false),
 * regardless of whether a site key is configured.
 *
 * When enabled but site key is missing, logs an error and returns empty string
 * (Turnstile will be disabled as a safe fallback).
 */
export function getTurnstileSiteKey(): string {
  if (!isTurnstileEnabled()) {
    return "";
  }

  // Build-time env (CRA)
  if (process.env.REACT_APP_GRANTS_TURNSTILE_SITE_KEY) {
    return process.env.REACT_APP_GRANTS_TURNSTILE_SITE_KEY;
  }

  // Runtime env (Docker inject-env.sh)
  if (
    typeof window !== "undefined" &&
    window._env_?.REACT_APP_GRANTS_TURNSTILE_SITE_KEY
  ) {
    return window._env_.REACT_APP_GRANTS_TURNSTILE_SITE_KEY;
  }

  console.error(
    "[Turnstile] REACT_APP_GRANTS_TURNSTILE_ENABLED=true but REACT_APP_GRANTS_TURNSTILE_SITE_KEY is missing. Turnstile will be disabled."
  );
  return "";
}
